import { useEffect } from "react";

type Handler = (event: KeyboardEvent) => void;

function isTypingTarget(target: EventTarget | null) {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

/**
 * Global keyboard shortcuts. Handlers are skipped while the user is typing
 * unless the shortcut explicitly allows it.
 */
export function useHotkeys(
  bindings: Array<{
    key: string;
    ctrlOrMeta?: boolean;
    shift?: boolean;
    allowInInputs?: boolean;
    handler: Handler;
  }>,
) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      for (const binding of bindings) {
        if (event.key.toLowerCase() !== binding.key.toLowerCase()) continue;
        if (Boolean(binding.ctrlOrMeta) !== (event.ctrlKey || event.metaKey)) continue;
        if (Boolean(binding.shift) !== event.shiftKey) continue;
        if (!binding.allowInInputs && isTypingTarget(event.target)) continue;
        event.preventDefault();
        binding.handler(event);
        return;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [bindings]);
}
